import * as React from "react";
import { homeLink, request } from "../../../../../services/cpln";
import { NGProviderBrowser, formatSizeForProviderBrowser } from "../../../ngProviderBrowser";
import { Mk8sInput } from "../../../../../schema/types/mk8s/input/mk8s";
import { Button, Card, Space, Tooltip } from "antd";
import { CardTitle } from "../../../../../components/antd/card/CardTitle";
import { CardContent } from "../../../../../components/antd/card/CardContent";
import { v4 as uuidv4 } from "uuid";
import { inputToMk8s } from "../../../draft/utils/inputToMk8s";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { CpFormItem } from "../../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../../../../gvc/testNewForm/CpErrorWarning";
import { CpInput } from "../../../../gvc/testNewForm/CpInput";
import { CpArrayObject } from "../../../../gvc/testNewForm/CpArrayObject";
import { CpArrayItemTaint } from "../aws/CpArrayItemTaint";
import { CpArrayItemNameValuePair } from "../../../../workload/draft/CpArrayItemNameValuePair";
import { store } from "../../../../../store";
import { useCpFormContext } from "../../../../../components/form/useCpFormContext";
import { EllipsisOutlined } from "@ant-design/icons";
import { CpInputNumber } from "../../../../gvc/testNewForm/CpInputNumber";
import { CpErrorWarningArray } from "../../../../gvc/testNewForm/CpErrorWarningArray";
import { CpErrorWarningArrayIcon } from "../../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { NodePoolDetectedTags } from "../detectedTags";
import { NodePoolCoreButton } from "../nodePoolCoreButton";

interface Props {
  index: number;
}
export const LinodeNodePool: React.FC<Props> = ({ index }) => {
  const { id: formId } = useCpFormContext();

  const region = useCpFormValue(`linode.region`);
  const name = useCpFormValue(`linode.nodePools.${index}.name`);
  const serverType = useCpFormValue(`linode.nodePools.${index}.serverType`);
  const overrideImage = useCpFormValue(`linode.nodePools.${index}.overrideImage`);
  const subnetId = useCpFormValue(`linode.nodePools.${index}.subnetId`);

  const [browserKey, setBrowserKey] = React.useState("");

  return (
    <>
      <div className="cpln-form-section cpln-ant-tab-content">
        <Card>
          <CardTitle>
            Node Pool{name ? ` | ${name}` : ""}
            <CpErrorWarningArrayIcon names={[`linode.nodePools`]} />
          </CardTitle>
          <CardContent>
            <div>
              <NodePoolDetectedTags name={`linode.nodePools.${index}.labels`} />
              <CpFormItem>
                <CpLabel name={`linode.nodePools.${index}.name`}>Name</CpLabel>
                <CpInput name={`linode.nodePools.${index}.name`} style={{ maxWidth: 600 }} />
                <CpErrorWarning name={`linode.nodePools.${index}.name`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`linode.nodePools.${index}.serverType`}>Server Type</CpLabel>
                <Space.Compact>
                  <CpInput name={`linode.nodePools.${index}.serverType`} style={{ width: 600 }} />
                  <Tooltip title={`Browse`}>
                    <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("server-types")} />
                  </Tooltip>
                </Space.Compact>
                <CpErrorWarning name={`linode.nodePools.${index}.serverType`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`linode.nodePools.${index}.subnetId`}>Subnet ID</CpLabel>
                <Space.Compact>
                  <CpInput name={`linode.nodePools.${index}.subnetId`} style={{ width: 600 }} />
                  <Tooltip title={`Browse`}>
                    <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("subnets")} />
                  </Tooltip>
                </Space.Compact>
                <CpErrorWarning name={`linode.nodePools.${index}.subnetId`} />
              </CpFormItem>
              <CpFormItem>
                <div className="flex gap-2">
                  <div className="flex flex-col">
                    <CpLabel name={`linode.nodePools.${index}.minSize`}>Min Size</CpLabel>
                    <CpInputNumber name={`linode.nodePools.${index}.minSize`} />
                  </div>
                  <div className="flex flex-col">
                    <CpLabel name={`linode.nodePools.${index}.maxSize`}>Max Size</CpLabel>
                    <CpInputNumber name={`linode.nodePools.${index}.maxSize`} />
                  </div>
                </div>
                <CpErrorWarningArray
                  names={[`linode.nodePools.${index}.minSize`, `linode.nodePools.${index}.maxSize`]}
                />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`linode.nodePools.${index}.overrideImage`}>Override Image</CpLabel>
                <Space.Compact>
                  <CpInput name={`linode.nodePools.${index}.overrideImage`} style={{ width: 600 }} />
                  <Tooltip title={`Browse`}>
                    <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("images")} />
                  </Tooltip>
                </Space.Compact>
                <CpErrorWarning name={`linode.nodePools.${index}.overrideImage`} />
              </CpFormItem>
              <div style={{ maxWidth: 900 }}>
                <CpFormItem>
                  <div className="flex items-center gap-4">
                    <CpLabel name={`skip`}>Labels</CpLabel>
                    <NodePoolCoreButton name={`linode.nodePools.${index}.labels`} />
                  </div>
                  <div className="-mt-2">
                    <CpArrayObject
                      factoryFn={() => ({ id: uuidv4(), name: "", value: "" })}
                      labels={["Name", "Value"]}
                      name={`linode.nodePools.${index}.labels`}
                    >
                      <CpArrayItemNameValuePair />
                    </CpArrayObject>
                  </div>
                </CpFormItem>
              </div>
              <div style={{ maxWidth: 900 }}>
                <CpArrayObject
                  factoryFn={() => ({ id: uuidv4(), key: "", value: "", effect: "" })}
                  labels={["Key", "Value", "Effect"]}
                  label="Taints"
                  name={`linode.nodePools.${index}.taints`}
                >
                  <CpArrayItemTaint />
                </CpArrayObject>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      {browserKey === "server-types" ? (
        <NGProviderBrowser
          // TODO pagination for all discovery endpoints with pages
          title="Browse Linode Server Types"
          initialValue={serverType}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "server-types",
                cluster: inputToMk8s(values),
              },
            });
            return data.data;
          }}
          sortable
          onOk={(value) => {
            formSetValue(formId, `linode.nodePools.${index}.serverType`, value as string);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          filteredColumns={["Name"]}
          initialSort={["Cost per Month", "asc"]}
          rightAlign={["Cost per Month"]}
          monoColumns={["Cost per Month"]}
          labels={["Name", "Cost per Month", "Id", "Cores", "Memory", "Disk", "GPUs"]}
          distinctFilters={["Cores", "Memory", "Disk", "GPUs"]}
          getData={(item: any) => {
            let priceObject = item.region_prices.find((rp) => rp.id === region);
            if (!priceObject) {
              priceObject = item.price;
            }
            const price = priceObject.monthly;
            const cost = price.toLocaleString("en-US", { style: "currency", currency: "USD" });

            return [
              item.id,
              item.label,
              [price, cost],
              item.id,
              item.vcpus,
              [item.memory, formatSizeForProviderBrowser(item.memory, "MB")],
              [item.disk, formatSizeForProviderBrowser(item.disk, "MB")],
              item.gpus,
            ];
          }}
        />
      ) : null}
      {browserKey === "images" ? (
        <NGProviderBrowser
          title="Browse Linode Images"
          initialValue={overrideImage}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "images",
                cluster: inputToMk8s(values),
              },
            });
            return data.data;
          }}
          onOk={(value) => {
            formSetValue(formId, `linode.nodePools.${index}.overrideImage`, value as string);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Description"]}
          getData={(item: any) => [item.id, item.id, item.label]}
        />
      ) : null}
      {browserKey === "subnets" ? (
        <NGProviderBrowser
          title="Browse Linode Subnets"
          initialValue={subnetId}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "subnets",
                cluster: inputToMk8s(values),
              },
            });
            return data.data;
          }}
          onOk={(value) => {
            formSetValue(formId, `linode.nodePools.${index}.subnetId`, String(value as string));
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Label", "Id", "IPv4"]}
          getData={(item: any) => [item.id, item.label, item.id, item.ipv4]}
        />
      ) : null}
    </>
  );
};
